// Admin Marketing — 5 tabs: Pulso · Contenido · Campañas · Decisiones IA · Estrategia
// Control central del Agente de Marketing (capa 2 de NULED OS)

import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';

type Tab = 'pulso' | 'contenido' | 'campanas' | 'decisiones' | 'estrategia';

type Pulse = {
  spend_today_mxn: number;
  conversions_today: number;
  visits_today: number;
  messages_today: number;
  quotes_today: number;
  orders_today: number;
  revenue_today_mxn: number;
  active_campaigns: number;
  pending_approvals: number;
};

type CampaignRoas = {
  id: string;
  name: string;
  platform: string;
  status: string;
  funnel_goal: string;
  daily_budget_mxn: number;
  spend_30d_mxn: number;
  conversions_30d: number;
  impressions_30d: number;
  cpl_mxn: number | null;
  revenue_30d_mxn: number;
  roas_30d: number | null;
  external_id: string | null;
};

type Decision = {
  id: string;
  decision_type: string;
  trigger: string;
  reasoning: string;
  inputs: any;
  outputs: any;
  status: string;
  confidence: number;
  model_used: string;
  created_at: string;
};

type Post = {
  id: string;
  platform: string;
  post_type: string;
  caption: string;
  status: string;
  needs_human_approval: boolean;
  approval_reason: string | null;
  scheduled_at: string | null;
  published_at: string | null;
  created_at: string;
};

export function AdminMarketing() {
  const [tab, setTab] = useState<Tab>('pulso');

  return (
    <div style={{ padding: 32, fontFamily: theme.font.sans }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: theme.textMuted, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>
            Marketing · Agente IA Autopilot
          </div>
          <h1 style={{ fontFamily: theme.font.head, fontSize: 28, fontWeight: 600, color: theme.brand, margin: '6px 0 0' }}>
            Marketing
          </h1>
        </div>
        <ActionButtons />
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${theme.border}`, marginBottom: 24 }}>
        {(['pulso', 'contenido', 'campanas', 'decisiones', 'estrategia'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 18px', fontSize: 12, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5,
            background: 'transparent',
            color: tab === t ? theme.brand : theme.textMuted,
            border: 'none', borderBottom: tab === t ? `3px solid ${theme.accent}` : '3px solid transparent',
            cursor: 'pointer', fontFamily: theme.font.sans,
          }}>
            {labelFor(t)}
          </button>
        ))}
      </div>

      {tab === 'pulso'      && <Pulso />}
      {tab === 'contenido'  && <Contenido />}
      {tab === 'campanas'   && <Campanas />}
      {tab === 'decisiones' && <Decisiones />}
      {tab === 'estrategia' && <Estrategia />}
    </div>
  );
}

function labelFor(t: Tab) {
  return { pulso: 'Pulso', contenido: 'Contenido', campanas: 'Campañas', decisiones: 'Decisiones IA', estrategia: 'Estrategia' }[t];
}

// ===== ACCIONES MANUALES =====
function ActionButtons() {
  const [running, setRunning] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const trigger = async (mode: string) => {
    setRunning(mode);
    setLastResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('marketing-agent', { body: { mode } });
      if (error) throw error;
      setLastResult(`${mode}: ${data.decisions_count} decisiones · ${data.summary?.slice(0, 80) ?? 'OK'}`);
    } catch (e: any) {
      setLastResult(`Error: ${e.message}`);
    } finally {
      setRunning(null);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      {lastResult && <div style={{ fontSize: 11, color: theme.textMuted, maxWidth: 320 }}>{lastResult}</div>}
      <button onClick={() => trigger('daily_content')} disabled={!!running} style={btnStyle}>
        {running === 'daily_content' ? '…' : '+ Contenido hoy'}
      </button>
      <button onClick={() => trigger('optimize')} disabled={!!running} style={btnStyle}>
        {running === 'optimize' ? '…' : 'Optimizar ahora'}
      </button>
      <button onClick={() => trigger('weekly_strategy')} disabled={!!running} style={{ ...btnStyle, background: theme.brand, color: '#fff' }}>
        {running === 'weekly_strategy' ? '…' : 'Plan semanal (Opus)'}
      </button>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  padding: '8px 14px', fontSize: 11, fontWeight: 700,
  background: theme.accent, color: theme.brand, border: 'none',
  textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer',
  fontFamily: theme.font.sans,
};

// ===== TAB: PULSO =====
function Pulso() {
  const [pulse, setPulse] = useState<Pulse | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('marketing_pulse_today').select('*').maybeSingle();
      if (data) setPulse(data as Pulse);
    })();
  }, []);

  if (!pulse) return <div style={{ color: theme.textMuted }}>Cargando…</div>;

  const cpl = pulse.conversions_today > 0 ? pulse.spend_today_mxn / pulse.conversions_today : 0;
  const roas = pulse.spend_today_mxn > 0 ? pulse.revenue_today_mxn / pulse.spend_today_mxn : 0;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        <Kpi label="Gasto hoy" value={`$${pulse.spend_today_mxn.toFixed(2)}`} unit="MXN" />
        <Kpi label="Conversiones" value={String(pulse.conversions_today)} sub={cpl > 0 ? `CPL $${cpl.toFixed(0)}` : '—'} />
        <Kpi label="Visitas" value={String(pulse.visits_today)} />
        <Kpi label="Mensajes WA" value={String(pulse.messages_today)} />
        <Kpi label="Cotizaciones" value={String(pulse.quotes_today)} />
        <Kpi label="Pedidos" value={String(pulse.orders_today)} sub={pulse.revenue_today_mxn > 0 ? `$${pulse.revenue_today_mxn.toFixed(0)} MXN` : ''} />
        <Kpi label="ROAS hoy" value={roas > 0 ? `${roas.toFixed(2)}x` : '—'} accent={roas >= 1.5} />
        <Kpi label="Campañas activas" value={String(pulse.active_campaigns)} />
        <Kpi label="Pendientes OK" value={String(pulse.pending_approvals)} accent={pulse.pending_approvals > 0} />
      </div>
    </div>
  );
}

function Kpi({ label, value, unit, sub, accent }: { label: string; value: string; unit?: string; sub?: string; accent?: boolean }) {
  return (
    <div style={{
      padding: 18, background: '#fff', border: `1px solid ${theme.border}`,
      borderLeft: `4px solid ${accent ? theme.accent : theme.borderStrong}`,
    }}>
      <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: theme.brand, marginTop: 6, fontFamily: theme.font.head, display: 'flex', alignItems: 'baseline', gap: 4 }}>
        {value}
        {unit && <span style={{ fontSize: 11, color: theme.textMuted, fontWeight: 400 }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ===== TAB: CONTENIDO =====
function Contenido() {
  const [posts, setPosts] = useState<Post[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('marketing_posts').select('*').order('created_at', { ascending: false }).limit(60);
      setPosts((data ?? []) as Post[]);
    })();
  }, []);

  const approve = async (id: string) => {
    await supabase.from('marketing_posts').update({
      status: 'scheduled', needs_human_approval: false, approved_at: new Date().toISOString(),
    }).eq('id', id);
    setPosts(p => p.map(x => x.id === id ? { ...x, status: 'scheduled', needs_human_approval: false } : x));
  };

  const reject = async (id: string) => {
    await supabase.from('marketing_posts').update({ status: 'archived' }).eq('id', id);
    setPosts(p => p.filter(x => x.id !== id));
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {posts.length === 0 && <div style={{ color: theme.textMuted }}>Sin posts aún. Click "Contenido hoy" para que el agente genere.</div>}
      {posts.map(p => (
        <div key={p.id} style={{ padding: 16, background: '#fff', border: `1px solid ${theme.border}`, borderLeft: `4px solid ${badgeColor(p.status)}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 10, padding: '2px 6px', background: badgeColor(p.status), color: '#fff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{p.status}</span>
              <span style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', fontWeight: 700 }}>{p.platform} · {p.post_type}</span>
              {p.scheduled_at && <span style={{ fontSize: 10, color: theme.textMuted }}>📅 {new Date(p.scheduled_at).toLocaleString('es-MX')}</span>}
            </div>
            {p.needs_human_approval && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => approve(p.id)} style={{ ...btnStyle, padding: '4px 10px' }}>OK</button>
                <button onClick={() => reject(p.id)} style={{ ...btnStyle, padding: '4px 10px', background: '#fff', color: theme.text, border: `1px solid ${theme.borderStrong}` }}>Rechazar</button>
              </div>
            )}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: theme.text, whiteSpace: 'pre-wrap' }}>{p.caption}</div>
          {p.approval_reason && (
            <div style={{ marginTop: 8, fontSize: 11, color: theme.warn, padding: 8, background: '#FEF3C7', borderLeft: `3px solid ${theme.warn}` }}>
              ⚠️ {p.approval_reason}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ===== TAB: CAMPAÑAS =====
type PendingAction = { id: string; action: 'publish' | 'activate' | 'pause' };

function Campanas() {
  const [rows, setRows] = useState<CampaignRoas[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<PendingAction | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('marketing_campaign_roas').select('*').order('spend_30d_mxn', { ascending: false });
    const roas = (data ?? []) as CampaignRoas[];
    const { data: ext } = await supabase.from('marketing_campaigns').select('id, external_id');
    const extMap: Record<string, string | null> = {};
    for (const e of (ext ?? []) as { id: string; external_id: string | null }[]) extMap[e.id] = e.external_id;
    setRows(roas.map(r => ({ ...r, external_id: extMap[r.id] ?? null })));
  };

  useEffect(() => { load(); }, []);

  const runAction = async (id: string, action: 'publish' | 'activate' | 'pause') => {
    setConfirming(null);
    setBusy(id);
    setMsg(null);
    try {
      const payload: Record<string, unknown> = { action, campaign_id: id };
      if (action === 'publish' || action === 'activate') payload.confirm = true;
      const { data, error } = await supabase.functions.invoke('meta-publisher', { body: payload });
      if (error) {
        let detail = error.message;
        try {
          const ctx = (error as any).context;
          const j = ctx && ctx.json ? await ctx.json() : null;
          if (j && j.error) detail = j.error;
        } catch { /* noop */ }
        throw new Error(detail);
      }
      if (data && data.error) throw new Error(data.error);
      setMsg(action === 'publish'
        ? `Publicada en Meta EN PAUSA (${data.meta_campaign_id}). Actívala para empezar a gastar.`
        : action === 'activate' ? 'Campaña ACTIVADA en Meta — ya está gastando.' : 'Campaña pausada en Meta.');
      await load();
    } catch (e: any) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const syncMetrics = async () => {
    setSyncing(true);
    setMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke('meta-insights-sync', { body: { days: 30 } });
      if (error) throw error;
      if (data && data.error) throw new Error(data.error);
      setMsg(`Métricas sincronizadas: ${data.rows_upserted ?? 0} filas · ${data.synced ?? 0} campañas.`);
      await load();
    } catch (e: any) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const actionCell = (r: CampaignRoas) => {
    if (confirming && confirming.id === r.id) {
      const isMoney = confirming.action !== 'pause';
      return (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: theme.warn, fontWeight: 700 }}>{isMoney ? '¿Gastar $?' : '¿Pausar?'}</span>
          <button onClick={() => runAction(r.id, confirming.action)} style={{ ...btnStyle, padding: '4px 8px', fontSize: 10, background: theme.err, color: '#fff' }}>Sí</button>
          <button onClick={() => setConfirming(null)} style={{ ...btnStyle, padding: '4px 8px', fontSize: 10, background: '#fff', color: theme.text, border: `1px solid ${theme.borderStrong}` }}>No</button>
        </div>
      );
    }
    if (busy === r.id) return <span style={{ fontSize: 10, color: theme.textMuted }}>…</span>;
    if (!r.external_id) {
      if (r.status === 'draft') {
        return <button onClick={() => setConfirming({ id: r.id, action: 'publish' })} style={{ ...btnStyle, padding: '4px 10px', fontSize: 10 }}>Publicar en Meta</button>;
      }
      return <span style={{ fontSize: 10, color: theme.textMuted }}>—</span>;
    }
    if (r.status === 'active') {
      return <button onClick={() => setConfirming({ id: r.id, action: 'pause' })} style={{ ...btnStyle, padding: '4px 10px', fontSize: 10, background: '#fff', color: theme.text, border: `1px solid ${theme.borderStrong}` }}>Pausar</button>;
    }
    return <button onClick={() => setConfirming({ id: r.id, action: 'activate' })} style={{ ...btnStyle, padding: '4px 10px', fontSize: 10, background: theme.ok, color: '#fff' }}>Activar</button>;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
        <div style={{ fontSize: 11, color: msg && msg.startsWith('Error') ? theme.err : theme.textMuted, flex: 1 }}>{msg}</div>
        <button onClick={syncMetrics} disabled={syncing} style={{ ...btnStyle, padding: '6px 12px', fontSize: 11, background: '#fff', color: theme.brand, border: `1px solid ${theme.borderStrong}` }}>
          {syncing ? 'Sincronizando…' : '↻ Sincronizar métricas Meta'}
        </button>
      </div>
      {rows.length === 0 && <div style={{ color: theme.textMuted }}>Aún no hay campañas. El agente las creará en weekly_strategy o puedes lanzar manualmente.</div>}
      <div style={{ background: '#fff', border: `1px solid ${theme.border}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: theme.brand, color: '#fff' }}>
            <tr>
              <th style={th}>Campaña</th>
              <th style={th}>Plataforma</th>
              <th style={th}>Goal</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: 'right' }}>Budget/día</th>
              <th style={{ ...th, textAlign: 'right' }}>Spend 30d</th>
              <th style={{ ...th, textAlign: 'right' }}>Conv</th>
              <th style={{ ...th, textAlign: 'right' }}>CPL</th>
              <th style={{ ...th, textAlign: 'right' }}>ROAS</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <td style={td}>
                  {r.name}
                  {r.external_id && <div style={{ fontSize: 9, color: theme.textMuted, fontFamily: theme.font.mono }}>Meta {r.external_id}</div>}
                </td>
                <td style={td}>{r.platform}</td>
                <td style={td}>{r.funnel_goal ?? '—'}</td>
                <td style={td}>
                  <span style={{ fontSize: 10, padding: '2px 6px', background: badgeColor(r.status), color: '#fff', fontWeight: 700, textTransform: 'uppercase' }}>{r.status}</span>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>${Number(r.daily_budget_mxn ?? 0).toFixed(0)}</td>
                <td style={{ ...td, textAlign: 'right' }}>${Number(r.spend_30d_mxn ?? 0).toFixed(0)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{r.conversions_30d}</td>
                <td style={{ ...td, textAlign: 'right', color: (r.cpl_mxn ?? 0) > 300 ? theme.err : theme.text }}>{r.cpl_mxn ? `$${r.cpl_mxn.toFixed(0)}` : '—'}</td>
                <td style={{ ...td, textAlign: 'right', color: (r.roas_30d ?? 0) >= 1.5 ? theme.ok : theme.textMuted, fontWeight: 700 }}>{r.roas_30d ? `${r.roas_30d.toFixed(2)}x` : '—'}</td>
                <td style={td}>{actionCell(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== TAB: DECISIONES =====
function Decisiones() {
  const [rows, setRows] = useState<Decision[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('marketing_decisions').select('*').order('created_at', { ascending: false }).limit(80);
      setRows((data ?? []) as Decision[]);
    })();
  }, []);

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {rows.length === 0 && <div style={{ color: theme.textMuted }}>Sin decisiones registradas todavía.</div>}
      {rows.map(d => (
        <div key={d.id} style={{
          padding: 14, background: '#fff', border: `1px solid ${theme.border}`,
          borderLeft: `4px solid ${d.status === 'awaiting_approval' ? theme.warn : d.status === 'failed' ? theme.err : theme.ok}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: theme.brand, textTransform: 'uppercase', letterSpacing: 0.5 }}>{d.decision_type}</span>
              <span style={{ fontSize: 10, color: theme.textMuted }}>← {d.trigger}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: theme.textMuted, fontFamily: theme.font.mono }}>{d.model_used}</span>
              <span style={{ fontSize: 10, color: theme.textMuted }}>conf {(d.confidence * 100).toFixed(0)}%</span>
              <span style={{ fontSize: 10, color: theme.textMuted }}>{new Date(d.created_at).toLocaleString('es-MX')}</span>
            </div>
          </div>
          <div style={{ fontSize: 12, color: theme.text, lineHeight: 1.5 }}>{d.reasoning}</div>
          {d.status === 'awaiting_approval' && (
            <div style={{ marginTop: 8, fontSize: 11, color: theme.warn, fontWeight: 700 }}>⏳ Esperando tu aprobación</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ===== TAB: ESTRATEGIA =====
function Estrategia() {
  const [latest, setLatest] = useState<Decision | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('marketing_decisions')
        .select('*').eq('trigger', 'weekly_plan')
        .order('created_at', { ascending: false }).limit(1).maybeSingle();
      if (data) setLatest(data as Decision);
    })();
  }, []);

  return (
    <div>
      <div style={{ padding: 18, background: '#fff', border: `1px solid ${theme.border}`, borderLeft: `4px solid ${theme.accent}` }}>
        <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
          Último plan semanal (Opus 4.7)
        </div>
        {latest ? (
          <>
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>
              {new Date(latest.created_at).toLocaleString('es-MX')}
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: theme.text, marginTop: 12, whiteSpace: 'pre-wrap' }}>
              {latest.reasoning}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 12 }}>
            Aún no se ha generado un plan semanal. Click el botón "Plan semanal (Opus)" arriba para generar el primero.
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Helpers =====
function badgeColor(status: string): string {
  if (status === 'active' || status === 'executed' || status === 'published') return theme.ok;
  if (status === 'paused' || status === 'scheduled') return theme.info;
  if (status === 'draft') return theme.textMuted;
  if (status === 'approval_pending' || status === 'awaiting_approval') return theme.warn;
  if (status === 'failed' || status === 'rejected') return theme.err;
  return theme.textMuted;
}

const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 };
const td: React.CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
